<script setup>
import { ref, computed, onMounted, onDeactivated } from 'vue';
import PopupButtons from './CardRows/PopupButtons.vue';
import Image from './CardRows/Image.vue';
import Logo from './CardRows/Logo.vue';
import List from './CardRows/List.vue';
import Icon from './CardRows/Icon.vue';
import FeatureList from './CardRows/FeatureList.vue';
import StatCards from './CardRows/StatCards.vue';
import Testimonial from './CardRows/Testimonial.vue';
import Nudges from './CardRows/Nudges.vue';
import Quiz from './CardRows/Quiz.vue';
import Survey from './CardRows/Survey.vue';
import TextInput from './CardRows/TextInput.vue';
import Reflection from './CardRows/Reflection.vue';
import Slider from './CardRows/Slider.vue';
import Text from './CardRows/Text.vue';
import Heading from './CardRows/Heading.vue';
import Video from './CardRows/Video.vue';
import Youtube from './CardRows/Youtube.vue';
import Title from './CardRows/Title.vue';
import ButtonLayout from './CardRows/ButtonLayout.vue';
import Confidence from './CardRows/Confidence.vue';
import ConfidenceConfirmation from './CardRows/Confidence.vue';
import MultiPageForm from './CardRows/MultiPageForm.vue';

import ActivityTracking from '@/Pages/Playlist/components/ActivityTracking.vue'
import { usePlaylistStore } from "@/stores/playlist.js";
const plStore = usePlaylistStore();
const isIntersecting = ref(false);

const card = computed(() => plStore.cards[props.index]);

const props = defineProps({
    index: {
        type: Number,
        default: undefined,
    },
    cardId: {
        type: Number,
        default: undefined,
    },
    playlistId: {
        type: Number,
        default: undefined,
    },
    brand: {
        type: Object,
        default: undefined,
    },
    scormExport: Boolean
})

const components = {
    PopupButtons,
    Image,
    Logo,
    List,
    Icon,
    FeatureList,
    StatCards,
    Testimonial,
    Nudges,
    Quiz,
    Survey,
    Slider,
    Text,
    TextInput,
    Reflection,
    Heading,
    Video,
    Youtube,
    Title,
    ButtonLayout,
    Confidence,
    ConfidenceConfirmation,
    MultiPageForm,
};

let observer;

onMounted(() => {
    const currIndex = props.index
    const currSlide = document.querySelector("[data-slide-index='" + currIndex + "']")
    const slideParent = currSlide.parentElement

    observer = new IntersectionObserver(items => {
        items.forEach(item => {
            if (item.isIntersecting) {
                isIntersecting.value = true;
            } else {
                isIntersecting.value = false;
            }
        });
    }, {
        threshold: 0.9,
        root: slideParent,
    });

    observer.observe(currSlide)
});

onDeactivated(() => {
    observer.disconnect();
})

</script>

<template>
    <ActivityTracking :index="index" :card-id="cardId" :scorm-export="scormExport" />
    <div
        v-if="card"
        class="cardContainer relative h-full flex flex-col"
        :class="[
            card.alignment == 'start' ? 'justify-start' : '',
            card.alignment == 'center' ? 'justify-center' : '',
            card.alignment == 'end' ? 'justify-end' : '',
            card.alignment == 'between' ? 'justify-between' : '',
        ]"
        :style="card.theme?.css"
    >
        <template v-for="( row, idx ) in card.rows " :key="idx">
            <component
                :is="components[row.type]"
                :is-intersecting="isIntersecting"
                :row="row"
                :card-index="props.index"
                :playlist-id="playlistId"
                :logo="card.theme?.content.logo"
                :theme-logo-align="card.theme?.content.logoAlign"
                :theme-logo-size="card.theme?.content.logoSize"
                :brand="brand"
                :shift="card.colour_shift || 0"
                :scorm-export="scormExport"
            />
        </template>
    </div>
</template>

<style scoped>
    @reference "../../../../css/app.css";

    .cardContainer {
        background: var(--card_bg);
        background-image: var(--bg_image);
        background-repeat: var(--bg_repeat, no-repeat);
        background-size: var(--bg_size, cover);
        background-position: var(--bg_position, center);
        font-family: var(--font);
        color: var(--card_text);
        @apply text-fl-base;
    }

    .cardContainer button {
        border-color: var(--button_bg);
        background-color: var(--button_bg);
        color: var(--button_text);
    }

    .cardContainer button:hover {
        border-color: var(--button_hover_bg);
        background-color: var(--button_hover_bg);
        color: var(--button_hover_text);
    }
</style>